// @flow

import * as React from "react";
import {
  compose,
  setDisplayName,
  withHandlers,
  withStateHandlers
} from "recompose";
import { getLabelForOrderMethodStatus } from "../../../../../helpers";
import {
  markOrderAsNoLongerOnHold,
  markOrderAsScreenPrintingCompleted,
  markOrderAsScreenPrintingStarted
} from "../../../../../graph";
import { withStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import JobStatusIcon from "../../../../../components/JobStatusIcon";
import PlaceOrderOnHoldModal from "../../../../../components/PlaceOrderOnHoldModal";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../../../components/withSnackbar";

import type { HOC } from "recompose";

type Props = {|
  +classes?: {|
    +buttonContainer: string,
    +jobStatus: string,
    +spaceBetweenContainer: string,
    +spacerRight: string,
    +spinnerWrapper: string
  |},
  +order: {|
    +canViewerCompleteScreenPrinting: boolean,
    +canViewerPlaceOnHold: boolean,
    +canViewerRemoveHold: boolean,
    +canViewerStartScreenPrinting: boolean,
    +id: string,
    +screenPrintingStatus: string
  |}
|};

type State = {|
  isMutatingOrder: boolean,
  isPlacingOrderOnHold: boolean
|};

const styles = theme => ({
  buttonContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(1)
  },

  jobStatus: {
    marginLeft: theme.spacing(1)
  },

  spaceBetweenContainer: {
    display: "flex"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  }
});

const enhancer: HOC<*, Props> = compose(
  setDisplayName("ScreenPrintingOrderStatus"),

  markOrderAsNoLongerOnHold,

  markOrderAsScreenPrintingCompleted,

  markOrderAsScreenPrintingStarted,

  withSnackbar,

  withStyles(styles),

  withStateHandlers(
    ({ isMutatingOrder: false, isPlacingOrderOnHold: false }: State),
    {
      setIsMutatingOrder: () => (isMutatingOrder: boolean) => ({
        isMutatingOrder
      }),

      setIsPlacingOrderOnHold: () => (isPlacingOrderOnHold: boolean) => ({
        isPlacingOrderOnHold
      })
    }
  ),

  withHandlers({
    handleHoldButtonClick: ({ setIsPlacingOrderOnHold }) => () => {
      setIsPlacingOrderOnHold(true);
    },

    handlePlaceOrderOnHoldModalRequestClose: ({
      setIsPlacingOrderOnHold
    }) => () => {
      setIsPlacingOrderOnHold(false);
    },

    handleRemoveHoldButtonClick: ({
      markOrderAsNoLongerOnHold,
      order: { id: orderId },
      setIsMutatingOrder,
      showErrorSnackbar,
      showSuccessSnackbar
    }) => () => {
      setIsMutatingOrder(true);
      markOrderAsNoLongerOnHold({ variables: { orderId } })
        .then(
          ({
            data: {
              markOrderAsNoLongerOnHold: { succeeded, errors }
            }
          }) => {
            if (succeeded) {
              showSuccessSnackbar(`The order has been updated`);
            } else {
              showErrorSnackbar(
                `Something went wrong: ${errors.orderId.join(", ")}`
              );
            }
          }
        )
        .catch(e => {
          showErrorSnackbar(`Something went wrong: ${e.message}`);
        })
        .finally(() => setIsMutatingOrder(false));
    },

    handleScreenPrintingCompletedButtonClick: ({
      markOrderAsScreenPrintingCompleted,
      order: { id: orderId },
      setIsMutatingOrder,
      showErrorSnackbar,
      showSuccessSnackbar
    }) => () => {
      setIsMutatingOrder(true);
      markOrderAsScreenPrintingCompleted({ variables: { orderId } })
        .then(
          ({
            data: {
              markOrderAsScreenPrintingCompleted: { succeeded, errors }
            }
          }) => {
            if (succeeded) {
              showSuccessSnackbar(`The order has been updated`);
            } else {
              showErrorSnackbar(
                `Something went wrong: ${errors.orderId.join(", ")}`
              );
            }
          }
        )
        .catch(e => {
          showErrorSnackbar(`Something went wrong: ${e.message}`);
        })
        .finally(() => setIsMutatingOrder(false));
    },

    handleStartButtonClick: ({
      markOrderAsScreenPrintingStarted,
      order: { id: orderId },
      setIsMutatingOrder,
      showErrorSnackbar,
      showSuccessSnackbar
    }) => () => {
      setIsMutatingOrder(true);
      markOrderAsScreenPrintingStarted({ variables: { orderId } })
        .then(
          ({
            data: {
              markOrderAsScreenPrintingStarted: { succeeded, errors }
            }
          }) => {
            if (succeeded) {
              showSuccessSnackbar(`The order has been updated`);
            } else {
              showErrorSnackbar(
                `Something went wrong: ${errors.orderId.join(", ")}`
              );
            }
          }
        )
        .catch(e => {
          showErrorSnackbar(`Something went wrong: ${e.message}`);
        })
        .finally(() => setIsMutatingOrder(false));
    }
  })
);

const ScreenPrintingOrderStatus = ({
  classes,
  handleScreenPrintingCompletedButtonClick,
  handleHoldButtonClick,
  handlePlaceOrderOnHoldModalRequestClose,
  handleRemoveHoldButtonClick,
  handleStartButtonClick,
  isMutatingOrder,
  isPlacingOrderOnHold,
  order: {
    canViewerCompleteScreenPrinting,
    canViewerPlaceOnHold,
    canViewerRemoveHold,
    canViewerStartScreenPrinting,
    screenPrintingStatus,
    id: orderId
  }
}) => (
  <div>
    <div className={classes.spaceBetweenContainer}>
      <JobStatusIcon status={screenPrintingStatus} />
      <div className={classes.jobStatus}>
        <Typography variant="h6">Screen Printing</Typography>
        <Typography variant="body2">
          {getLabelForOrderMethodStatus(screenPrintingStatus)}
        </Typography>
      </div>
    </div>
    {isMutatingOrder ? (
      <div className={classes.spinnerWrapper}>
        <CircularProgress />
      </div>
    ) : (
      <div className={classes.buttonContainer}>
        {canViewerStartScreenPrinting && (
          <Button
            className={classes.spacerRight}
            color="primary"
            variant="contained"
            onClick={handleStartButtonClick}
          >
            Start
          </Button>
        )}
        {canViewerCompleteScreenPrinting && (
          <Button
            className={classes.spacerRight}
            color="primary"
            variant="contained"
            onClick={handleScreenPrintingCompletedButtonClick}
          >
            Screen Printing Complete
          </Button>
        )}
        {canViewerPlaceOnHold && (
          <Button
            color="secondary"
            onClick={handleHoldButtonClick}
            variant="contained"
          >
            Hold
          </Button>
        )}
        {canViewerRemoveHold && (
          <Button
            color="secondary"
            onClick={handleRemoveHoldButtonClick}
            variant="contained"
          >
            Remove Hold
          </Button>
        )}
      </div>
    )}
    <PlaceOrderOnHoldModal
      isOpen={isPlacingOrderOnHold}
      onRequestClose={handlePlaceOrderOnHoldModalRequestClose}
      orderId={orderId}
    />
  </div>
);

export default enhancer(ScreenPrintingOrderStatus);
